// game/shared/src/collision-core.ts
//
// Pure runtime-collision geometry, SHARED between the authoritative server
// (HelloRoom collision pass — see server/src/collision.ts, which re-exports
// this module) and the client's local-hero prediction (net/hero-prediction.ts).
// Moved out of the server in the 2026-07-02 pathfinding pass: the predicted
// hero used to be ejected only out of decorative doodads, while the server also
// ejects out of buildings, resource nodes and other units — every class the
// prediction couldn't mirror was a rubber-band source (render sinks into the
// obstacle, server holds the true position at its rim, the soft correction
// drags the hero back). One geometry module means the two sides can never
// disagree about what is solid.
//
// Everything here is side-effect-free and operates on plain numbers, so it
// unit-tests without a Colyseus room (see server qa-collision.ts).

import { BUILDING_DEFS, type BuildingType } from "./buildings";

/** Unit collision radius. KTS KoboldShaman UnitData.xml radius = 0.625; rounded
 *  to 0.6 so two units settle ~1.2u apart (just inside melee, never overlapping). */
export const UNIT_RADIUS = 0.6;

/**
 * Whether a building's footprint physically blocks a unit on `unitTeamId`.
 *
 * Semantics (honours the two footprint flags in `buildings.ts`):
 *   - `blocksEnemies: true`  → one-sided wall: blocks ENEMIES only, allies pass.
 *     (Palisade, AutomatedDefenseTurret — lets a team wall itself in.)
 *   - else `blocksPath: true` → solid structure: blocks EVERYONE (Forge, Tavern,
 *     Wonder, …). You path around it regardless of ownership.
 *   - else                    → walk-through (Tent, Campfire, SpikeTrap, Farm).
 *
 * Destroyed buildings never block. Constructing buildings DO block — the
 * footprint is reserved the moment placement validates.
 */
export function buildingBlocks(
  buildingType: string,
  buildingTeamId: number,
  buildingState: string,
  unitTeamId: number,
): boolean {
  if (buildingState === "destroyed") return false;
  const def = BUILDING_DEFS[buildingType as BuildingType];
  if (!def) return false;
  if (def.footprint.blocksEnemies) return unitTeamId !== buildingTeamId;
  return def.blocksPath;
}

export interface BoxPushResult {
  x: number;
  z: number;
  pushed: boolean;
}

/**
 * Eject a point of radius `radius` from an axis-aligned box centred at (bx,bz)
 * with half-extents (halfX,halfZ). Pushes along the axis of LEAST penetration
 * so the unit slides along the wall it hit instead of popping over a corner.
 * Returns the input unchanged when the point is already clear.
 */
export function pushOutOfBox(
  px: number, pz: number,
  bx: number, bz: number,
  halfX: number, halfZ: number,
  radius: number,
): BoxPushResult {
  const ex = halfX + radius;
  const ez = halfZ + radius;
  const dx = px - bx;
  const dz = pz - bz;
  const penX = ex - Math.abs(dx);
  const penZ = ez - Math.abs(dz);
  // Outside the expanded box on at least one axis → no overlap.
  if (penX <= 0 || penZ <= 0) return { x: px, z: pz, pushed: false };
  if (penX < penZ) {
    const sign = dx >= 0 ? 1 : -1; // dead-centre (dx===0) ejects to +X
    return { x: bx + sign * ex, z: pz, pushed: true };
  }
  const sign = dz >= 0 ? 1 : -1;
  return { x: px, z: bz + sign * ez, pushed: true };
}

/**
 * Resource-node collision radii (runtime ejection). The startup navmesh bake
 * carves box footprints so PATHFINDING routes around nodes, but a unit that
 * paths to a point clipping a node's corner (or a node type the bake missed)
 * could still end up standing inside it — this pass is the hard guarantee, the
 * same belt-and-suspenders role the building pushout plays. Radii ≈ the navmesh
 * footprint half-extent (UNIT_FOOTPRINTS / 2) and stay < GATHER_RANGE − UNIT_RADIUS
 * so a player ejected to the rim is still inside gather range. Kinds absent here
 * (creature-drop pickups) are walk-through by design.
 */
export const RESOURCE_RADII: Record<string, number> = {
  MapleTree: 0.5,
  ElderTree: 0.75,
  StoneDeposit: 1.0,
  GoldDeposit: 1.0,
  BerryBush: 0.5,
  CulinaryHerbBush: 0.5,
};

/**
 * Eject a point of radius `radius` from a static circular obstacle centred at
 * (ox,oz) with radius `obstacleRadius`. Pushes radially out to the contact
 * circle. Coincident centres eject along +X so the normal is never NaN.
 */
export function pushOutOfCircle(
  px: number, pz: number,
  ox: number, oz: number,
  obstacleRadius: number,
  radius: number,
): BoxPushResult {
  const minDist = obstacleRadius + radius;
  const dx = px - ox;
  const dz = pz - oz;
  let d = Math.hypot(dx, dz);
  if (d >= minDist) return { x: px, z: pz, pushed: false };
  let nx: number, nz: number;
  if (d < 1e-6) { nx = 1; nz = 0; } else { nx = dx / d; nz = dz / d; }
  return { x: ox + nx * minDist, z: oz + nz * minDist, pushed: true };
}

export interface PushoutPass {
  x: number;
  z: number;
  /** True if ANY obstacle ejected the point during this pass. */
  pushed: boolean;
}

/**
 * Iterate a one-pass pushout to a fixed point, then clamp the result back onto
 * the navmesh. This is the orchestration that fixes the bot/player chasm-and-corner
 * wedge (2026-06-20 navmesh-trap report); the room supplies `onePass` (a single
 * sweep over all its static obstacles) and `nearestPoint` (recast). Kept pure +
 * side-effect-free so it unit-tests without a Colyseus room (see qa-collision.ts).
 *
 *   - Fixed-point iteration (Cause 2): obstacles are resolved sequentially, so at
 *     a concave corner ejecting out of A pushes into B and vice-versa. Re-running
 *     the whole sweep until nothing moves (or `maxPasses`) settles the corner
 *     instead of leaving the point embedded and oscillating frame-to-frame.
 *   - Navmesh clamp (Cause 1): pushout ejects toward open space, but at a chasm
 *     rim / map edge that space is the void (no navmesh). If the settled point is
 *     more than `clampTolSq` (squared) off the nearest navmesh poly, snap it back
 *     on-mesh. `nearestPoint` MUST return its input unchanged when no poly is near
 *     (recast's findNearestPoly miss) so an already-on-mesh point clamps by ≈0 and
 *     is left alone; the tolerance sits above the navmesh erosion margin so a unit
 *     legitimately ejected to an obstacle rim isn't yanked.
 *
 * The CLIENT prediction reuses this with an identity `nearestPoint` and an
 * Infinite tolerance (it has no navmesh) — same fixed-point settling, no clamp.
 */
export function settleAndClamp(
  startX: number,
  startZ: number,
  onePass: (x: number, z: number) => PushoutPass,
  nearestPoint: (x: number, z: number) => { x: number; z: number },
  clampTolSq: number,
  maxPasses = 4,
): { x: number; z: number } {
  let x = startX;
  let z = startZ;
  for (let pass = 0; pass < maxPasses; pass++) {
    const r = onePass(x, z);
    x = r.x;
    z = r.z;
    if (!r.pushed) break;
  }
  if (x !== startX || z !== startZ) {
    const np = nearestPoint(x, z);
    const offSq = (np.x - x) ** 2 + (np.z - z) ** 2;
    if (offSq > clampTolSq) {
      x = np.x;
      z = np.z;
    }
  }
  return { x, z };
}

export interface PairPushResult {
  ax: number; az: number;
  bx: number; bz: number;
  pushed: boolean;
}

/**
 * Separate two unit centres so they sit at least `2*radius` apart.
 *
 * Push distribution is one-sided by intent (playtest feedback 2026-06-12: "you
 * can't shove another entity by walking into it"). The `aMovable`/`bMovable`
 * flags say which body the caller permits to move this tick — in production a
 * cube is movable iff it still has an active path tracker (i.e. it's the one
 * walking). The whole overlap correction goes to the movable body so a STILL
 * unit is never displaced; the mover absorbs it all and ends up flush against
 * the stationary one (reads as "stops/slides"):
 *   - both movable  → split evenly (legacy behaviour; the default).
 *   - one movable   → that body takes the full correction, the other stays put.
 *   - neither       → split evenly anyway, so two stacked-at-spawn idlers still
 *                     unstack instead of overlapping forever.
 * Exact-overlap (distance ≈ 0) ejects along +X/−X so the two never share a NaN
 * normal.
 */
export function resolvePair(
  ax: number, az: number,
  bx: number, bz: number,
  radius: number,
  aMovable = true,
  bMovable = true,
): PairPushResult {
  const minDist = radius * 2;
  const dx = ax - bx;
  const dz = az - bz;
  let d = Math.hypot(dx, dz);
  if (d >= minDist) return { ax, az, bx, bz, pushed: false };
  let nx: number, nz: number;
  if (d < 1e-6) { nx = 1; nz = 0; d = 0; } // coincident → arbitrary axis
  else { nx = dx / d; nz = dz / d; }
  const overlap = minDist - d;
  // Distribute the correction between the two bodies per movability.
  let aShare: number, bShare: number;
  if (aMovable && bMovable) { aShare = overlap / 2; bShare = overlap / 2; }
  else if (aMovable)        { aShare = overlap;     bShare = 0; }
  else if (bMovable)        { aShare = 0;           bShare = overlap; }
  else                      { aShare = overlap / 2; bShare = overlap / 2; }
  return {
    ax: ax + nx * aShare, az: az + nz * aShare,
    bx: bx - nx * bShare, bz: bz - nz * bShare,
    pushed: true,
  };
}
